import { batch, bind, classNames, Component, h, signal, tag } from "omi";
import isEmpty from "lodash-es/isEmpty";

import globalStyles from "@/globals/styles.css?inline";
import { getElementsFromSlot } from "@/utils/elements.ts";
import { NWButton, NWDivider, NWIcon } from "@/wc/components";
import { Store } from "@/store";
import { tailwind } from "@/tailwind";

import { NWSectionProPaywall } from "./variants/pro";
import {
  ExtraTaskVariantProps,
  isExtraTaskVariant,
  isProVariant,
  isSectionVariant,
  NWSectionProps,
  NWSectionStatus,
  NWSectionType,
  ProVariantProps,
  SectionVariantProps,
} from "./models.ts";
import { NWStep } from "../nw-step";

@tag("nw-section")
export class NWSection extends Component<NWSectionProps> {
  static css = [tailwind, globalStyles];
  static propTypes = {
    id: String,
    type: String,
    // SECTION type attributes
    stepid: String,
    heading: String,
    name: String,
    isopen: Boolean,
    // PRO type attributes
    contentId: String,
    status: String,
  };

  private isProSection = false;
  private isOpen = signal(false);
  private boxIds = signal<string[]>([]);

  install() {
    batch(() => {
      this.isProSection = isProVariant(this.props);
      this.isOpen.value = isSectionVariant(this.props) || isExtraTaskVariant(this.props) ? this.props.isopen : false;
    });
  }

  installed() {
    this.boxIds.value = this.getBoxIds();
  }

  uninstall(): void {
    this.boxIds.value = [];
  }

  /**
   * Retrieves all box IDs from nw-validation-box children within this component
   * @returns An array of box IDs from child validation boxes
   */
  @bind
  private getBoxIds(): string[] {
    const elements = getElementsFromSlot<Element>(this.shadowRoot, "extra-task-slot");
    if (!elements) {
      return [];
    }

    const validationBoxes = elements.filter(element => element.tagName === "NW-VALIDATION-BOX");

    const boxIds = Array.from(validationBoxes)
      .map(box => box.getAttribute("id"))
      .filter((id): id is string => !isEmpty(id));

    return boxIds;
  }

  @bind
  private handleOpenToggle() {
    const desiredState = !this.isOpen.value;
    this.isOpen.value = desiredState;
    this.fire("toggle-isopen", { isOpen: desiredState });

    import("@/services/analyticsService").then(({ useAnalyticsService }) => {
      useAnalyticsService().trackSectionToggled(this.props.id, desiredState ? "opened" : "closed");
    });
  }

  @bind
  private renderProSection(props: ProVariantProps) {
    if (!Store.user.isPro() || props.status !== NWSectionStatus.UNLOCKED) {
      return <NWSectionProPaywall {...props}></NWSectionProPaywall>;
    }

    return (
      <div class="animate-in fade-in slide-in-from-bottom-6 ease-out" style="animation-duration: 2800ms;" id={props.id}>
        <slot></slot>
      </div>
    );
  }

  private renderSection(props: SectionVariantProps) {
    return (
      <div id={props.id}>
        <NWStep id={props.stepid} title={props.name}>
          <div className="flex items-center justify-between">
            {props.heading}
            <NWButton size="icon" type="icon" onClick={this.handleOpenToggle}>
              <NWIcon
                name="chevron-down"
                width={16}
                height={16}
                className={classNames("size-4 transition-all duration-300 shrink-0 text-quaternary", {
                  "rotate-180": this.isOpen.value,
                  "rotate-0": !this.isOpen.value,
                })}
              />
            </NWButton>
          </div>
        </NWStep>
        <div
          class={classNames("grid transition-all duration-300 ease-in-out", {
            "grid-rows-[0fr] opacity-0": !this.isOpen.value,
            "grid-rows-[1fr] opacity-100": this.isOpen.value,
          })}
        >
          <div class="overflow-hidden">
            <slot></slot>
          </div>
        </div>
      </div>
    );
  }

  private renderExtraTaskSection(props: ExtraTaskVariantProps) {
    return (
      <div id={props.id}>
        <NWDivider padding="sm">
          <NWButton
            size="fit"
            type="secondary"
            icon={this.isOpen.value ? "minus-circle" : "plus-circle"}
            onClick={this.handleOpenToggle}
          >
            {this.isOpen.value ? "Remove Task" : "Challenge yourself"}
          </NWButton>
        </NWDivider>
        <div
          class={classNames("grid transition-all duration-300 ease-in-out", {
            "grid-rows-[0fr] opacity-0": !this.isOpen.value,
            "grid-rows-[1fr] opacity-100": this.isOpen.value,
          })}
        >
          <div class="overflow-hidden">
            <slot id="extra-task-slot"></slot>
          </div>
        </div>
      </div>
    );
  }

  render(props: NWSectionProps) {
    if (props.type === NWSectionType.EXTRA_TASK) {
      return this.renderExtraTaskSection(props);
    }

    if (this.isProSection) {
      return this.renderProSection(props as ProVariantProps);
    }

    return this.renderSection(props as SectionVariantProps);
  }
}
